<template>
  <div
    class="achievement-notification"
    :class="[`rarity-${achievement.rarity}`, { 'with-particles': showParticles }]"
    @click="close"
  >
    <div class="notification-content">
      <div class="achievement-icon">{{ achievementIcon }}</div>
      <div class="achievement-details">
        <h3 class="achievement-title">{{ achievement.achievementName || achievement.name }}</h3>
        <p class="achievement-description">{{ achievement.description }}</p>
        <p class="achievement-trigger">
          Triggered by visitor <strong>{{ formatSessionId(achievement.triggeredBySessionId) }}</strong>
        </p>
      </div>
      <button class="close-button" @click.stop="close" aria-label="Close notification">×</button>
    </div>

    <!-- Particle effects for rare/legendary achievements -->
    <div v-if="showParticles" class="particles">
      <div v-for="n in 20" :key="n" class="particle" :style="getParticleStyle(n)" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import type { Achievement } from '@ephemera/shared';

interface Props {
  achievement: Achievement;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
}>();

const achievementIcon = computed(() => {
  // Icon based on rarity
  switch (props.achievement.rarity) {
    case 'legendary':
      return '🏆';
    case 'rare':
      return '💎';
    case 'uncommon':
      return '⭐';
    case 'common':
    default:
      return '🎉';
  }
});

const showParticles = computed(
  () => props.achievement.rarity === 'rare' || props.achievement.rarity === 'legendary'
);

function formatSessionId(sessionId: string): string {
  // Format session ID for display
  return sessionId || 'Unknown';
}

function close() {
  emit('close');
}

function getParticleStyle(index: number) {
  // Random positioning and animation delay for particles
  const angle = (360 / 20) * index;
  const distance = 50 + Math.random() * 50;
  const delay = Math.random() * 0.5;

  return {
    '--angle': `${angle}deg`,
    '--distance': `${distance}px`,
    '--delay': `${delay}s`,
  };
}

onMounted(() => {
  // Auto-dismiss based on rarity (per IMPLEMENTATION_PLAN.md lines 722-735)
  const durations = {
    common: 3000,
    uncommon: 5000,
    rare: 8000,
    legendary: 12000,
  };

  setTimeout(() => {
    emit('close');
  }, durations[props.achievement.rarity]);
});
</script>

<style scoped>
.achievement-notification {
  position: relative;
  background: var(--color-sepia-light);
  border-radius: 8px;
  padding: var(--spacing-lg);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 2px solid var(--color-sepia-dark);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  min-width: 350px;
  max-width: 450px;
  overflow: hidden;
}

.achievement-notification:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

/* Rarity-based border colors */
.achievement-notification.rarity-common {
  border-color: var(--color-common);
}

.achievement-notification.rarity-uncommon {
  border-color: var(--color-uncommon);
}

.achievement-notification.rarity-rare {
  border-color: var(--color-rare);
  background: linear-gradient(135deg, var(--color-sepia-light) 0%, #f0e8f0 100%);
}

.achievement-notification.rarity-legendary {
  border-color: var(--color-legendary);
  background: linear-gradient(135deg, var(--color-sepia-light) 0%, #fff4e6 100%);
  box-shadow: 0 4px 20px rgba(212, 175, 55, 0.4);
}

.notification-content {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  position: relative;
  z-index: 2;
}

.achievement-icon {
  font-size: 2.5rem;
  flex-shrink: 0;
  animation: bounce 0.6s ease-out;
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.achievement-details {
  flex: 1;
}

.achievement-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 var(--spacing-xs) 0;
  color: var(--color-black-primary);
  font-family: var(--font-serif);
}

.achievement-description {
  font-size: 0.95rem;
  margin: 0 0 var(--spacing-sm) 0;
  color: var(--color-black-secondary);
}

.achievement-trigger {
  font-size: 0.85rem;
  color: var(--color-black-tertiary);
  margin: 0;
}

.achievement-trigger strong {
  color: var(--color-burgundy);
  font-family: monospace;
}

.close-button {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: var(--color-black-secondary);
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: color 0.2s;
}

.close-button:hover {
  color: var(--color-black-primary);
}

/* Particle effects for rare/legendary */
.particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.particle {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-legendary);
  opacity: 0;
  animation: particle-burst 1.5s ease-out var(--delay, 0s) forwards;
}

.rarity-rare .particle {
  background: var(--color-rare);
}

@keyframes particle-burst {
  0% {
    opacity: 1;
    transform: translate(0, 0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(
        calc(cos(var(--angle)) * var(--distance)),
        calc(sin(var(--angle)) * var(--distance))
      )
      scale(0.5);
  }
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .achievement-notification {
    min-width: 280px;
    max-width: 100%;
    padding: var(--spacing-md);
  }

  .achievement-title {
    font-size: 1.1rem;
  }

  .achievement-icon {
    font-size: 2rem;
  }
}
</style>
